/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  getRowIdxForId,
  getLocalStorageTablePref,
  updateTablePreferenceLocalStorage,
  diffListOfObjects,
  getCommonAttributes,
} from '../utils/index';
import { getAndFormat30DaysAgo } from '../utils/formatters';
import ClientTextField from '../components/clientFields/ClientTextField';
import ClientDropDown from '../components/clientFields/ClientDropDown';
import MPAutocomplete from '../components/clientFields/MPAutocomplete';
import logger from '../utils/logger';
import APIWrapper from '../utils/graphqlwrapper';
import { codeColumns } from '../utils/schemaInfo';
import AppContext from '../context';
import { createCodeRows } from '../utils/tableHelpers';
import AdvancedSearchV2 from '../components/AdvancedSearchV2';
import AdvancedSearchChip from '../components/AdvancedSearchChip';
import ServerTable from '../components/ServerTable';
import Loading from '../components/Loading';

const initColumnVisibilityModel = { from_goop: false, createdAt: false };
const structure = {
  type: [
    'Select one',
    'acquisition',
    'revenue',
    'acquisition_d2d',
    'revenue_d2d',
    'Fundraising',
    'Joint_Action',
    'Bundling',
    'Survey',
    'Other',
    'Petition',
    'Action',
    'p2p',
    'broadcast',
  ],
  channel: [ // used to populate sms channel options
    'Select one',
    'Blue Action',
    'Durable',
    'Grassroots Analytics',
    'Hockeystick',
    'HouseFile',
    'Leverage',
    'Message Digital',
    'MissionSMS',
    'Mobile Commons',
    'ROI/Ripple',
    'Scale to Win',
    'Sterling',
    'Strive',
    'Switchboard',
    'Tatango',
    'Textify',
    'Other',
  ],
};

function CodeGroupings(props) {
  const { client } = props;
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initColumnVisibilityModel);
  const [snackMessage, setSnackMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackWithConfirm, setSnackWithConfirm] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  // tracks data in create/edit modal
  const [editRowData, setEditRowData] = useState({});
  const [bulkEditFormData, setBulkEditFormData] = useState({});
  // codes modified for preview/bulk edit and to send to batchEditCodes mutation
  const [codesToUpdate, setCodesToUpdate] = useState([]);
  const [previewCodesAll, setPreviewCodesAll] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const { authenticated, setShouldLogOut } = useContext(AppContext);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceOptions, setSourceOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [codesPageState, setCodesPageState] = useState({
    page: 0, // mui pages are 0 indexed
    pageSize: 50,
    data: [],
    total: 0,
    isPreview: false,
    previewData: [],
  });

  const thirtyDaysAgoString = getAndFormat30DaysAgo();

  // niceley formatted query for our API
  const [advancedSearchQuery, setAdvancedSearchQuery] = useState({
    client_id: {
      eq: client,
    },
    and: {
      most_recent_transaction_date: {
        gte: thirtyDaysAgoString,
      },
      and: {
        or: [
          {
            source: {
              exists: 'false',
            },
          },
          {
            source: {
              eq: '',
            },
          },
        ],
      },
    },
  });

  // when we have user input from the MPAutocomplete component - we want to run a query
  const searchForSources = async (subString) => {
    /**
     * searches Sources table for a direct or partial match to the substring passed in
     *
     * @param {String} subString the search that the user has typed into the field
     * @returns {Array} an array of sources from the sources table that match the criteria
     */
    const filter = {
      client_id: { eq: client },
    };
    const wordList = subString.split(/[^a-zA-Z0-9]+/).filter(Boolean); // split on non-alphanumeric characters, remove empty strings
    const searchString = wordList.join('*');
    filter.and = {
      or: [
        { source: { eq: `${subString}` } }, // search for full string
        {
          or: [
            { source: { wildcard: `*${searchString}*` } }, // search for combo of words without spaces/non-alphanumeric characters
            { source: { match: `${subString}` } }, // search for straight substring match
          ],
        },
      ],
    };
    setSearching(true);
    const results = await APIWrapper.queryApi(
      { query: 'searchSources' },
      setShouldLogOut,
      {
        filter,
        limit: 500,
      },
    ).catch((err) => { logger.error(err); });
    setSearching(false);
    return results.data[0];
  };

  useEffect(() => {
    const getSourcesForDropdown = async () => {
      if (authenticated && client) {
        try {
          const results = await searchForSources(sourceSearch);
          const sources = [];
          results.forEach((item) => sources.push(`${item.source} - ${item.channel_category}`));
          setSourceOptions(sources);
        } catch (err) {
          logger.error(err);
        }
      }
    };
    getSourcesForDropdown();
  }, [sourceSearch, client, authenticated]);

  // onclick handler for edit icon in the table actions column
  const handleEditClick = (event, row) => {
    const curRow = row.row;
    if (!curRow.from_goop) {
      setEditRowData(curRow);
      setIsDialogOpen(true);
    } else {
      setSnackMessage('The source was created in Goop and so you need to update it in Goop.');
      setOpenSnackbar(true);
    }
  };

  // also clearing the form here in case the user exited the form by clicking off the form
  // in which case the values may still be populated bc form state was not cleared
  const handleBulkEditClick = () => {
    // clear existing values from form state
    const isPreview = false;
    const previewData = [];
    const formData = getCommonAttributes(selectedRows);
    setCodesPageState({ ...codesPageState, isPreview, previewData });
    setBulkEditFormData(formData);
    setCodesToUpdate([]);

    setBulkEditOpen(true);
  };

  const getPreviewData = (curCodesData, selectedRows_, bulkEditFormData_) => {
    const selectedRowIds = selectedRows_.map((row) => row.id);
    const previewData = [];
    const codesToEdit = [];
    // Only apply fields with non-empty values so we don't overwrite existing code data with blanks
    const nonEmptyBulkEdits = Object.fromEntries(
      Object.entries(bulkEditFormData_).filter(
        ([, v]) => v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === ''),
      ),
    );
    curCodesData.forEach((obj) => {
      if (selectedRowIds.indexOf(obj.id) > -1) {
        const updatedObj = { ...obj, ...nonEmptyBulkEdits };
        const codePatch = { id: obj.id, code: obj.code, ...nonEmptyBulkEdits };
        previewData.push(updatedObj);
        codesToEdit.push(codePatch);
      } else {
        previewData.push(obj);
      }
    });
    setCodesToUpdate(codesToEdit);
    setPreviewCodesAll(previewData);
    return previewData;
  };

  const validateInput = (row, requiredFields) => {
    /**
     * takes a row and checks whether the required fields have values
     * @param {Object} row the row being edited or the bulk edit data being previewed
     * @param {Array} requiredFields the fields that are required for this row to be valid
     * @returns {Array} an array of missing fields, empty if all required fields have values
     */
    const missingFields = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const field of requiredFields) {
      if (row[field] === null || row[field] === undefined || row[field] === '') {
        missingFields.push(field);
      }
    }
    return missingFields;
  };

  // made preview into a state for the base table (instead of creating a separate preview component)
  const togglePreview = () => {
    const missingFields = validateInput(bulkEditFormData, ['source']);
    if (missingFields.length > 0) {
      setSnackMessage('Source is required, please select a source to preview your changes.');
      setOpenSnackbar(true);
    } else {
      const isPreview = true;
      const previewData = getPreviewData(codesPageState.data, selectedRows, bulkEditFormData);
      setCodesPageState({ ...codesPageState, isPreview, previewData });
      setBulkEditOpen(false);
      setSnackMessage('Do you wish to save the previewed changes?');
      setSnackWithConfirm(true);
      setOpenSnackbar(true);
    }
  };

  // clear state for bulk edit form
  const handleBulkEditFormCancel = () => {
    const isPreview = false;
    const previewData = [];
    setCodesPageState({ ...codesPageState, isPreview, previewData });
    setBulkEditFormData({});
    setSelectedRows([]);
    setCodesToUpdate([]);
    setBulkEditOpen(false);
  };

  const actionColumns = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 75,
      disableClickEventBubbling: true,
      cellClassName: 'actions',
      disableReorder: true,
      getActions: (row) => [
        <GridActionsCellItem
          icon={<EditIcon color={row.row.from_goop ? 'disabled' : 'inherit'} />}
          label="Edit"
          className="textPrimary"
          onClick={(ev) => handleEditClick(ev, row)}
          color="inherit"
          size="medium"
        />,
      ],
    },
  ];

  const columns = actionColumns.concat(codeColumns);

  // part of component state; I would rather have it above but columns has
  // dependencies on methods defined above
  const [orderedColumns, setOrderedColumns] = useState(columns);

  const getErrorMessage = (errorLike, fallbackMessage) => {
    if (!errorLike) {
      return fallbackMessage;
    }

    const errs = Array.isArray(errorLike)
      ? errorLike
      : errorLike.errors;

    if (Array.isArray(errs) && errs.length > 0) {
      const messages = errs
        .map((item) => (item && item.message ? item.message : null))
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join(' ');
      }
    }

    if (errorLike.message) {
      return errorLike.message;
    }

    return fallbackMessage;
  };

  const saveFunc = (newRow) => {
    if (authenticated) {
      // default the revenue type to p2p for all SMS_Longcode codes
      const copyNewRow = { ...newRow };
      if (newRow.channel_category === 'SMS_Longcode') {
        copyNewRow.type = 'p2p';
      }
      copyNewRow.fixed_cost_cpa_override = newRow.fixed_cost_cpa_override
        ? parseFloat(newRow.fixed_cost_cpa_override) : null;
      copyNewRow.fixed_cost_media_spend = newRow.fixed_cost_media_spend ? parseFloat(
        newRow.fixed_cost_media_spend,
      ) : null;
      copyNewRow.most_recent_spend = newRow.most_recent_spend
        ? parseFloat(newRow.most_recent_spend) : null;

      // removing fields that aren't specified in input for the query/mutation
      const keysToDelete = [
        'client_id', 'code', 'most_recent_spend_date', 'most_recent_transaction_date', 'platforms_with_spend',
        'revenue', 'transactions', 'total_spend', 'updatedAt', 'createdAt', 'page', 'actblue_managing_entities',
      ];
      keysToDelete.forEach((key) => {
        delete copyNewRow[key];
      });
      APIWrapper.queryApi({ mutation: 'updateCodes' }, setShouldLogOut, {
        input: copyNewRow,
      })
        .then((resp) => {
          const fallbackMessage = 'Changes to the row could not be saved.';
          if (resp.errors && resp.errors.length) {
            setSnackMessage(getErrorMessage(resp.errors, fallbackMessage));
            setOpenSnackbar(true);
            setAdvancedSearchQuery((q) => ({ ...q }));
            return;
          }
          const results = (resp.data && resp.data[0] && resp.data[0][0]) ? resp.data[0][0] : null;
          if (results) {
            setSnackMessage(`${results.code} was updated.`);
            setOpenSnackbar(true);
          } else {
            setSnackMessage(fallbackMessage);
            setOpenSnackbar(true);
          }
        })
        .catch((err) => {
          logger.error(err);
          const errMsg = getErrorMessage(err, 'Changes to the row could not be saved.');
          setSnackMessage(errMsg);
          setOpenSnackbar(true);
          setAdvancedSearchQuery((q) => ({ ...q }));
        });
    }
  };

  // used for Snackbar (handles click Cancel and Close - X)
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    // if in preview state
    // (user clicks X to cancel the update/preview)
    if (codesPageState.isPreview) {
      setSelectedRows([]);
      setCodesPageState({ ...codesPageState, isPreview: false, previewData: [] });
      setCodesToUpdate([]);
      setBulkEditFormData({});
    }

    setOpenSnackbar(false);
    setSnackMessage('');
    setSnackWithConfirm(false);
  };

  // for dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleBulkEditClose = () => {
    setBulkEditOpen(false);
  };

  const handleModalSave = () => {
    /**
     * validates row being edited and triggers save if it is good
     */
    let missingFields;
    if (editRowData.channel_category === 'SMS_Longcode') {
      if (editRowData.channel === 'MissionSMS') {
        missingFields = validateInput(editRowData, ['source', 'version', 'channel', 'jira_ticket_num']);
      } else {
        missingFields = validateInput(editRowData, ['source', 'version', 'channel']);
      }
    } else {
      missingFields = validateInput(editRowData, ['source']);
    }
    if (missingFields.length > 0) {
      setSnackMessage(`You are missing some required fields, please make selections for the following fields: ${missingFields.join(', ')}`);
      setOpenSnackbar(true);
    } else {
      // update the row in the table manually
      const rowIdx = getRowIdxForId(editRowData.id, codesPageState.data);
      if (rowIdx > -1) {
        const copyRows = codesPageState.data.slice(0);
        copyRows.splice(rowIdx, 1, editRowData);

        setCodesPageState((old) => ({ ...old, data: copyRows }));
        saveFunc(editRowData);
        setIsDialogOpen(false);
      }
    }
  };

  const setter = (key, value, extra = null) => {
    /**
     * responds to onChange event in order to track updates to the row from the
     * create/edit modal/dialog; updates state in this component which will be
     * used to send to the graphql mutation
     * @param {String} key the name of the field being updated (e.g. source, audience, etc.)
     * @param {String} value the new value for the field being updated
     * @param {String} extra used for when we want to set an additional field based on the value
     * selected in the dropdown (e.g. setting channel category based on source selection)
     */
    if (key === 'source' && extra) {
      // if we are setting the source, we also want to set the channel category
      setEditRowData(
        // eslint-disable-next-line no-shadow
        (editRowData) => {
          if (extra === 'SMS_Longcode') {
            return {
              ...editRowData, channel_category: extra, [key]: value, type: 'p2p',
            };
          }
          return { ...editRowData, channel_category: extra, [key]: value };
        },
      );
    } else {
      // eslint-disable-next-line no-shadow
      setEditRowData((editRowData) => ({ ...editRowData, [key]: value }));
    }
  };

  const bulkEditsetter = (key, value, extra = null) => {
    /**
     * responds to onChange event in order to track updates to the row from the
     * create/edit modal/dialog; updates state in this component which will be
     * used to send to the graphql mutation
     * @param {String} key the name of the field being updated (e.g. source, audience, etc.)
     * @param {String} value the new value for the field being updated
     * @param {String} extra used for when we want to set an additional field based on the value
     * selected in the dropdown (e.g. setting channel category based on source selection)
     */
    if (key === 'source' && extra) {
      // if we are setting the source, we also want to set the channel category
      setBulkEditFormData(
        // eslint-disable-next-line no-shadow
        (bulkEditFormData) => {
          if (extra === 'SMS_Longcode') {
            return {
              ...bulkEditFormData, channel_category: extra, [key]: value, type: 'p2p',
            };
          }
          return { ...bulkEditFormData, channel_category: extra, [key]: value };
        },
      );
    } else {
      // eslint-disable-next-line no-shadow
      setBulkEditFormData((bulkEditFormData) => ({ ...bulkEditFormData, [key]: value }));
    }
  };

  // removing fields that aren't specified in input for the query/mutation
  const getCodeWithoutUnmutableFields = (code) => {
    const copyCode = { ...code };
    const keysToDelete = ['createdAt', 'client_id', 'code', 'fixed_cost_cpa_override', 'fixed_cost_media_spend', 'from_goop',
      'most_recent_spend', 'most_recent_spend_date', 'most_recent_transaction_date', 'platforms_with_spend', 'revenue', 'transactions', 'total_spend', 'page', 'actblue_managing_entities'];
    keysToDelete.forEach((key) => { delete copyCode[key]; });
    return copyCode;
  };

  const getPreviewSavedSummary = (status) => {
    /**
     * returns a list of rejected or fulfilled codes depending on the status parameter passed in.
     * @param {String} status rejected or fulfilled status from the lambda response
     * @returns {String} a comma separated list of codes
     */
    const msg = codesToUpdate.reduce((accum, cur) => {
      if (status === 'rejected') {
        if (cur.source === null) {
          return `${accum} ${cur.code},`;
        }
      } else if (status === 'fulfilled') {
        if (cur.source !== null) {
          return `${accum} ${cur.code},`;
        }
      }
      return `${accum}`;
    }, '');
    return msg;
  };

  const bulkUpdateFunc = () => {
    /**
     * Triggered when user confirms the previewed changes in the snackbar message. Takes the
     * list of codes to update, and sends them to the batchEditCodes lambda function for processing
     */
    if (authenticated) {
      // default revenue type to p2p if channel_category is SMS_Longcode
      const normalizedCodesToUpdate = codesToUpdate.map((code) => (
        code.channel_category === 'SMS_Longcode'
          ? { ...code, type: 'p2p' }
          : code
      ));

      const cleanedCodesToUpdate = normalizedCodesToUpdate.map((code) => getCodeWithoutUnmutableFields(code));
      const inputObj = { requests: cleanedCodesToUpdate };
      const input = JSON.stringify(inputObj);

      APIWrapper.queryApi(
        { query: 'batchEditCodes' },
        setShouldLogOut,
        { input },
      )
        .then((resp) => {
          // handle actual appsync errors
          if (resp.errors && resp.errors.length) {
            setSnackMessage('The previewed changes could not be saved. Please submit a tools help ticket so we can investigate the issue.');
            setOpenSnackbar(true);
            setSelectedRows([]);
            setCodesPageState({
              ...codesPageState, isPreview: false, previewData: [],
            });
            setCodesToUpdate([]);
            setBulkEditFormData({});
            setAdvancedSearchQuery((q) => ({ ...q }));
          } else {
            // handle information returned from the lambda/docclient failures and successes together
            const respData = JSON.parse(resp.data[0]);
            let fulfilledCount = 0;
            let rejectedCount = 0;
            const rejectedReasons = [];
            for (let i = 0; i < respData.length; i += 1) {
              const current = respData[i];
              if (current.status === 'fulfilled') {
                fulfilledCount += 1;
              } else if (current.status === 'rejected') {
                rejectedCount += 1;
                rejectedReasons.push(current.reason);
              }
            }
            let message = '';
            if (rejectedCount > 0) {
              const uniqueErrors = [...new Set(
                rejectedReasons
                  .flatMap((r) => r.split('; '))
                  .map((r) => r.replace(/ for id .+$/, '')),
              )];
              message += `${rejectedCount} code(s) failed to update: ${uniqueErrors.join('; ')}.`;
            }
            if (fulfilledCount > 0) {
              const fulList = getPreviewSavedSummary('fulfilled').slice(0, -1);
              message += ` Successfully updated the following codes:${fulList}.`;
            }
            setSnackMessage(message);
            setOpenSnackbar(true);
            setSelectedRows([]);
            setCodesPageState({
              ...codesPageState, data: rejectedCount > 0 ? [] : previewCodesAll, isPreview: false, previewData: [],
            });
            setCodesToUpdate([]);
            setBulkEditFormData({});
            if (rejectedCount > 0) {
              setAdvancedSearchQuery((q) => ({ ...q }));
            }
          }
        });
    }
  };

  const handleSnackBulkEditUpdateConfirm = () => {
    bulkUpdateFunc();
    setSnackWithConfirm(false);
    setOpenSnackbar(false);
    setSnackMessage('');
  };

  const handleSnackBulkEditUpdateBack = () => {
    if (codesPageState.isPreview) {
      setCodesPageState({ ...codesPageState, isPreview: false });
      setBulkEditOpen(true);
    }
    setSnackWithConfirm(false);
    setOpenSnackbar(false);
    setSnackMessage('');
  };

  const actionForBulkEditUpdate = (
    <>
      <Button color="secondary" size="small" onClick={handleSnackBulkEditUpdateConfirm}>
        Save
      </Button>
      <Button color="secondary" size="small" onClick={handleSnackBulkEditUpdateBack}>
        Back
      </Button>
      <Button color="secondary" size="small" onClick={handleClose}>
        Cancel
      </Button>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  const actionDefault = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  useEffect(() => {
    const codesColumnVisibility = getLocalStorageTablePref('codes', 'column-visibility');
    if (!codesColumnVisibility) {
      updateTablePreferenceLocalStorage('codes', 'column-visibility', initColumnVisibilityModel);
    } else {
      setColumnVisibilityModel(codesColumnVisibility);
    }
  }, []);

  useEffect(() => {
    const codesColumnsLocStore = getLocalStorageTablePref('codes', 'column');
    const isCodesColDefUpdated = !diffListOfObjects(codeColumns, codesColumnsLocStore);

    if (!codesColumnsLocStore || isCodesColDefUpdated) {
      updateTablePreferenceLocalStorage('codes', 'column', columns);
    } else {
      let elToUpdate = codesColumnsLocStore[0];
      elToUpdate = {
        ...elToUpdate,
        getActions: (row) => [
          <GridActionsCellItem
            icon={<EditIcon color={row.row.from_goop ? 'disabled' : 'inherit'} />}
            label="Edit"
            className="textPrimary"
            onClick={(ev) => handleEditClick(ev, row)}
            color="inherit"
            size="medium"
          />,
        ],
      };
      codesColumnsLocStore[0] = elToUpdate;
      setOrderedColumns(codesColumnsLocStore);
    }
  }, [codesPageState]);

  // used for chips
  const [userCriteria, setUserCriteria] = useState([]);

  const defaultSearch = [
    {
      col: 'source',
      op: 'exists',
      criteria: 'false',
      children: [
        {
          col: 'source',
          op: 'eq',
          criteria: '',
          id: 0,
        },
      ],
      id: 1,
    },
    {
      col: 'most_recent_transaction_date',
      op: 'gte',
      criteria: thirtyDaysAgoString,
      children: [],
      id: 3,
    },
  ];

  // search criteria user has entered in the advanced search
  const [currUserSearch, setCurrUserSearch] = useState(defaultSearch);

  const deleteChip = (parentIndex) => {
    setCurrUserSearch((prevState) => {
      const newState = [...prevState];
      newState.splice(parentIndex, 1);
      return newState;
    });
  };

  const defaultSort = {
    field: 'createdAt',
    direction: 'desc',
  };

  const [advancedSearchSort, setAdvancedSearchSort] = useState(defaultSort);

  // default/locked criteria the user can't delete - search must be scoped to current client
  const lockedCriteria = [
    {
      col: 'client_id',
      op: 'eq',
      criteria: client,
    },
  ];

  const getDefaultColumns = () => codeColumns;
  const isRowSelectable = (params) => !params.row.from_goop;

  useEffect(() => {
    document.title = 'MissionPortal | Codes';
  }, []);

  return (
    <>
      <div className="table-actions">
        <div className="batch-edit-btn-container">
          <Button
            variant="contained"
            onClick={handleBulkEditClick}
          >
            Bulk Edit
          </Button>
          <p>
            {selectedRows.length}
            {' '}
            selected
          </p>
        </div>
        <AdvancedSearchV2
          currUserSearch={currUserSearch}
          setCurrUserSearch={setCurrUserSearch}
          searchSetter={setAdvancedSearchQuery}
          sortSetter={setAdvancedSearchSort}
          lockedCriteria={lockedCriteria}
          defaultCriteria={defaultSearch}
          defaultSort={defaultSort}
          columns={codeColumns}
          lockedColumns={['client_id']}
          setUserChips={setUserCriteria}
        />
      </div>
      <AdvancedSearchChip
        columns={codeColumns}
        lockedCriteria={lockedCriteria}
        userCriteria={userCriteria}
        setUserCriteria={setUserCriteria}
        onDelete={(index) => { deleteChip(index); }}
      />
      <div style={{ flexGrow: 1 }}>
        {client && (
          <ServerTable
            pageState={codesPageState}
            updatePageState={setCodesPageState}
            columns={orderedColumns}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
            filters={advancedSearchQuery}
            sortModel={advancedSearchSort}
            queryName="searchCodes"
            tablePrefix="codes-adv"
            checkboxSelection
            setSelectedRows={setSelectedRows}
            createRows={createCodeRows}
            getDefaultColumns={getDefaultColumns}
            setOrderedColumns={() => { }} // ls/saving prefs is in flux, adding this to prevent errs
            isRowSelectable={isRowSelectable}
          />
        )}
      </div>

      <Dialog onClose={handleCloseDialog} open={isDialogOpen} maxWidth="sm" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', m: 1 }}>
          <DialogTitle>Editing Code</DialogTitle>
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleCloseDialog}
            sx={{ pr: 2 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            flexGrow: 1,
            p: 3,
            m: 1,
            borderRadius: 1,
          }}
        >
          <ClientTextField
            setFunction={setter}
            data={editRowData.client_id}
            attribute="client_id"
            label="Client"
            isDisabled
          />
          <ClientTextField
            setFunction={setter}
            data={editRowData.code}
            attribute="code"
            label="Code"
            isDisabled
          />
          <div className="codes-source-search-container">
            <MPAutocomplete
              setFunction={setter}
              dropdownOptions={sourceOptions}
              label={(
                <>
                  Source –
                  <strong> Channel Category*</strong>
                </>
              )}
              editRowData={editRowData}
              field="source"
              setSourceSearch={setSourceSearch}
            />
            {searching && <Loading />}
          </div>
          <ClientTextField
            setFunction={() => { }}
            data={editRowData.channel_category}
            attribute="channel_category"
            label="Channel Category"
            isDisabled
          />
          {(editRowData.channel_category === 'SMS_Longcode' || editRowData.channel_category === 'SMS_Shortcode') ? (
            <ClientDropDown
              setFunction={setter}
              data={editRowData.channel}
              attribute="channel"
              label="Channel"
              structure={structure}
              required
            />
          ) : (
            <ClientTextField
              setFunction={setter}
              data={editRowData.channel}
              attribute="channel"
              label="Channel"
            />
          )}
          {editRowData.channel_category === 'SMS_Longcode' && editRowData.channel === 'MissionSMS' && (
            <ClientTextField
              setFunction={setter}
              data={(editRowData && editRowData.jira_ticket_num) ? editRowData.jira_ticket_num : ''}
              attribute="jira_ticket_num"
              label="Jira Ticket Number"
              required
            />
          )}
          {editRowData.channel_category === 'SMS_Longcode' && (
            <>
              <ClientTextField
                setFunction={setter}
                data={(editRowData && editRowData.version) ? editRowData.version : ''}
                attribute="version"
                label="Version"
                required
              />
              <ClientTextField
                setFunction={setter}
                data={(editRowData && editRowData.send_cost) ? editRowData.send_cost : ''}
                attribute="send_cost"
                label="Send Cost"
                type="number"
              />
              <ClientTextField
                setFunction={setter}
                data={(editRowData && editRowData.send_size) ? editRowData.send_size : ''}
                attribute="send_size"
                label="Send Size"
                type="number"
              />
            </>

          )}
          <ClientTextField
            setFunction={setter}
            data={editRowData.audience}
            attribute="audience"
            label="Audience"
          />
          <ClientTextField
            setFunction={setter}
            data={editRowData.audience_category}
            attribute="audience_category"
            label="Audience Category"
          />
          <ClientTextField
            setFunction={setter}
            data={editRowData.revenue_category}
            attribute="revenue_category"
            label="Revenue Category"
          />
          {editRowData.channel_category === 'SMS_Longcode' ? (
            <p>Revenue Type will be automatically set to p2p for SMS_Longcode codes.</p>
          ) : (
            <ClientDropDown
              setFunction={setter}
              data={editRowData && editRowData.type ? editRowData.type : ''}
              attribute="type"
              label="Revenue Type"
              structure={structure}
            />
          )}
          {editRowData.channel_category !== 'SMS_Longcode' && (
            <>
              <ClientTextField
                setFunction={setter}
                data={editRowData.fixed_cost_cpa_override}
                attribute="fixed_cost_cpa_override"
                label="Fixed Cost CPA"
                type="number"
              />
              <ClientTextField
                setFunction={setter}
                data={editRowData.fixed_cost_media_spend}
                attribute="fixed_cost_media_spend"
                label="Fixed Cost Media Spend"
                type="number"
              />
              <ClientTextField
                setFunction={setter}
                data={editRowData.most_recent_spend}
                attribute="most_recent_spend"
                label="Most Recent Spend"
                type="number"
              />
            </>
          )}
          <Button
            className="datagrid-rightside-buttons"
            variant="contained"
            style={{
              justifyContent: 'center',
              marginTop: '20px',
              marginBottom: '15px',
            }}
            onClick={handleModalSave}
          >
            Save
          </Button>
        </Box>
      </Dialog>

      {selectedRows
        && (
          <Dialog onClose={handleBulkEditClose} open={bulkEditOpen} maxWidth="sm" fullWidth>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', m: 1 }}>
              <DialogTitle>
                Bulk Editing
                {' '}
                {selectedRows.length}
                {' '}
                Codes
              </DialogTitle>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleBulkEditClose}
                sx={{ pr: 2 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                flexGrow: 1,
                p: 3,
                m: 1,
                borderRadius: 1,
              }}
            >
              <div className="bulk-edit-codes-list">
                <p>Codes:</p>
                <ul>
                  {selectedRows.map((row) => <li key={row.code}>{row.code}</li>)}
                </ul>
              </div>
              <div className="codes-source-search-container">
                <MPAutocomplete
                  setFunction={bulkEditsetter}
                  dropdownOptions={sourceOptions}
                  editRowData={(bulkEditFormData && bulkEditFormData.source) ? bulkEditFormData : {}}
                  label={(
                    <>
                      Source –
                      <strong> Channel Category*</strong>
                    </>
                  )}
                  field="source"
                  setSourceSearch={setSourceSearch}
                />
                {searching && <Loading />}
              </div>
              <ClientTextField
                setFunction={() => { }}
                data={bulkEditFormData.channel_category}
                attribute="channel_category"
                label="Channel Category"
                isDisabled
              />
              {(bulkEditFormData.channel_category === 'SMS_Longcode' || bulkEditFormData.channel_category === 'SMS_Shortcode') ? (
                <ClientDropDown
                  setFunction={bulkEditsetter}
                  data={(bulkEditFormData && bulkEditFormData.channel) ? bulkEditFormData.channel : ''}
                  attribute="channel"
                  label="Channel"
                  structure={structure}
                />
              ) : (
                <ClientTextField
                  setFunction={bulkEditsetter}
                  data={bulkEditFormData.channel}
                  attribute="channel"
                  label="Channel"
                />
              )}
              {bulkEditFormData.channel_category === 'SMS_Longcode' && bulkEditFormData.channel === 'MissionSMS' && (
                <ClientTextField
                  setFunction={bulkEditsetter}
                  data={(bulkEditFormData && bulkEditFormData.jira_ticket_num) ? bulkEditFormData.jira_ticket_num : ''}
                  attribute="jira_ticket_num"
                  label="Jira Ticket Number"
                />
              )}
              {bulkEditFormData.channel_category === 'SMS_Longcode' && (
                <>
                  <ClientTextField
                    setFunction={bulkEditsetter}
                    data={bulkEditFormData.version}
                    attribute="version"
                    label="Version"
                  />
                  <ClientTextField
                    setFunction={bulkEditsetter}
                    data={(bulkEditFormData && bulkEditFormData.send_cost) ? bulkEditFormData.send_cost : ''}
                    attribute="send_cost"
                    label="Send Cost"
                    type="number"
                  />
                  <ClientTextField
                    setFunction={bulkEditsetter}
                    data={(bulkEditFormData && bulkEditFormData.send_size) ? bulkEditFormData.send_size : ''}
                    attribute="send_size"
                    label="Send Size"
                    type="number"
                  />
                </>

              )}
              <ClientTextField
                setFunction={bulkEditsetter}
                data={(bulkEditFormData && bulkEditFormData.audience) ? bulkEditFormData.audience : ''}
                attribute="audience"
                label="Audience"
              />
              <ClientTextField
                setFunction={bulkEditsetter}
                data={(bulkEditFormData && bulkEditFormData.audience_category) ? bulkEditFormData.audience_category : ''}
                attribute="audience_category"
                label="Audience Category"
              />
              <ClientTextField
                setFunction={bulkEditsetter}
                data={(bulkEditFormData && bulkEditFormData.revenue_category) ? bulkEditFormData.revenue_category : ''}
                attribute="revenue_category"
                label="Revenue Category"
              />
              {bulkEditFormData.channel_category === 'SMS_Longcode' ? (
                <p>Revenue Type will be automatically set to p2p for SMS_Longcode codes.</p>
              ) : (
                <ClientDropDown
                  setFunction={bulkEditsetter}
                  data={(bulkEditFormData && bulkEditFormData.type) ? bulkEditFormData.type : ''}
                  attribute="type"
                  label="Revenue Type"
                  structure={structure}
                />
              )}
              <Button
                className="datagrid-rightside-buttons"
                variant="contained"
                style={{
                  marginTop: '20px',
                  marginBottom: '15px',
                }}
                onClick={togglePreview}
              >
                Preview Changes
              </Button>
              <Button
                className="datagrid-rightside-buttons"
                variant="contained"
                style={{
                  marginTop: '20px',
                  marginBottom: '15px',
                }}
                onClick={handleBulkEditFormCancel}
              >
                Cancel
              </Button>
            </Box>
          </Dialog>
        )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={codesPageState.isPreview ? null : 6000}
        onClose={handleClose}
        message={snackMessage}
        action={snackWithConfirm ? actionForBulkEditUpdate : actionDefault}
      />
    </>
  );
}

export default CodeGroupings;

CodeGroupings.propTypes = {
  client: PropTypes.string,
};
